import { useState } from "react";
import {
  localDateAt,
  localTimeAt,
  type CancelOccurrenceRequest,
  type MoveOccurrenceRequest,
  type ScheduleEntry,
} from "@edu/shared";
import * as api from "../../lib/api.ts";

/**
 * Состояние и ручки отмены, переноса и возврата занятия серии, своего и
 * группового.
 *
 * Вынесено из `EntryTools.tsx` 08.10.2026, когда в карточку пришло завершение
 * серии с даты: функция с ним переваливала за потолок в сто пятьдесят строк
 * (раздел 9 CLAUDE.md). Это раскладка, а не новое поведение: тела запросов,
 * адрес занятия группы и вторая ступень отмены с уроком переехали без правок.
 *
 * ⚠️ **Хук живёт столько же, сколько `EntryTools`**, а не столько, сколько форма
 * режима, и зовётся там ДО ранних `return` (правило хуков). Причина та же, по
 * которой состояние раньше стояло в самом `EntryTools`: причина отмены,
 * набранная в одной форме, уезжает и с переносом, а живя в форме, стиралась бы
 * при каждой смене режима.
 *
 * `onDone` зовётся после успешной отмены и переноса и закрывает режим. Возврат
 * его не зовёт: возврат нажимают из ряда значков, режим там и так закрыт.
 */
export function useOccurrenceActions({
  entry,
  zone,
  run,
  onDone,
}: {
  entry: ScheduleEntry;
  zone: string;
  run: (fn: () => Promise<void>) => Promise<void>;
  onDone: () => void;
}) {
  /**
   * Умолчания формы переноса это ТЕКУЩЕЕ место занятия в зоне СЕРИИ, а не в
   * зоне смотрящего. Человек чаще двигает занятие на день-два, чем задаёт время
   * с нуля, а подставленное время наблюдателя молча перенесло бы занятие на
   * несколько часов от того, что он видел в поле.
   */
  const formZone = entry.seriesTimeZone ?? zone;
  const [reason, setReason] = useState("");
  const [toDate, setToDate] = useState(() => localDateAt(entry.at, formZone));
  const [toTime, setToTime] = useState(() => localTimeAt(entry.at, formZone));
  /** Урок уже подготовлен: отмена унесёт его в корзину, и это надо подтвердить. */
  const [confirmLesson, setConfirmLesson] = useState(false);

  /**
   * ⚠️ **Адрес занятия ГРУППЫ состоит из трёх частей, а не из двух.** Ключ
   * занятия тот же (`slotId` плюс исходная дата, слот здесь представитель
   * пучка), но перед ним стоит сама группа, и берётся она из строки сетки:
   * второго места, где известен `groupId`, у карточки нет.
   *
   * Кроме адреса и слов подтверждения не отличается ничего: групповые ручки
   * принимают буквально те же тела запроса, поэтому ни второго компонента, ни
   * второго набора полей даты и времени здесь не заводится.
   */
  const group = entry.group;
  const slotId = entry.slotId;
  const occursOn = entry.occursOn;
  // Ручки зовутся только из форм занятия серии, а `EntryTools` рисует их лишь
  // при обоих полях ключа. Проверка здесь сужает тип, а не меняет поведение
  const keyed = slotId !== null && occursOn !== null;

  const cancel = (force: boolean) =>
    void run(async () => {
      if (!keyed) return;
      const body: CancelOccurrenceRequest = {
        reason: reason.trim() === "" ? null : reason.trim(),
        ...(force ? { force: true } : {}),
      };
      try {
        if (group === undefined) await api.cancelOccurrence(slotId, occursOn, body);
        else await api.cancelGroupOccurrence(group.id, slotId, occursOn, body);
        onDone();
        setConfirmLesson(false);
      } catch (e) {
        if (e instanceof api.ApiError && e.code === "lesson_exists") {
          setConfirmLesson(true);
          return;
        }
        throw e;
      }
    });

  const move = () =>
    void run(async () => {
      if (!keyed) return;
      const body: MoveOccurrenceRequest = {
        toDate,
        toTime,
        reason: reason.trim() === "" ? null : reason.trim(),
      };
      if (group === undefined) await api.moveOccurrence(slotId, occursOn, body);
      else await api.moveGroupOccurrence(group.id, slotId, occursOn, body);
      onDone();
    });

  const restore = () =>
    void run(async () => {
      if (!keyed) return;
      if (group === undefined) await api.clearOccurrence(slotId, occursOn);
      else await api.clearGroupOccurrence(group.id, slotId, occursOn);
    });

  return {
    formZone,
    reason,
    setReason,
    toDate,
    setToDate,
    toTime,
    setToTime,
    confirmLesson,
    setConfirmLesson,
    cancel,
    move,
    restore,
  };
}
